import { Chip, DataTable, Icons, Panel } from "../../../../ds";
import type { Column } from "../../../../ds";
import { identityWebIamUrl, spaceScope } from "../../../locks/utils/identityWeb";

import { ExternalLinkButton } from "../../../locks/components/ExternalLinkButton";

interface RoleRow {
    role: string;
    lock?: boolean;
    allows: string;
}

const roles: RoleRow[] = [
    { role: "Ark Reader", allows: "Reads the pages of the space." },
    { role: "Ark Contributor", allows: "Adds, edits and locks pages, and proposes changes." },
    { role: "Ark Owner", allows: "Also approves change requests and edits these settings." },
    { role: "Lock Reader", lock: true, allows: "Sees one locked page and its children." },
    { role: "Lock Owner", lock: true, allows: "Also unlocks that page and manages its readers." },
];

const roleColumns: Column<RoleRow>[] = [
    {
        key: "role",
        header: "Role",
        width: "14rem",
        render: (row) => <Chip size="sm" tone="neutral" icon={row.lock ? <Icons.lock size={12} /> : undefined}>{row.role}</Chip>,
    },
    { key: "allows", header: "What it allows", render: (row) => row.allows },
];

/**
 * Permissions (OrbitalPlan/18 D8). Who may read and edit a space is an Identity Core assignment at the space's scope, managed in
 * Identity Web; Ark only records where locks are. This section explains that, names the five roles and links to the space's IAM page.
 */
export function PermissionsSection(props: { spaceId: string }) {
    const iamUrl = identityWebIamUrl(spaceScope(props.spaceId));
    return (
        <Panel
            title="Permissions"
            actions={iamUrl ? <ExternalLinkButton href={iamUrl} text="Manage access in Identity Web" size="md" /> : undefined}
        >
            <div className="ark-stack">
                <p className="ark-settings-sub">
                    Who can read and edit this space is managed in Identity, not in Ark: people and groups get an Ark role (Ark Reader,
                    Ark Contributor or Ark Owner) on the space, on Ark or on the whole platform. Locked pages add two roles of their own;
                    space Owners and platform admins read every locked page.
                </p>
                {!iamUrl ? (
                    <p className="ark-muted">Identity Web is not configured for this environment (App.IdentityWebUrl).</p>
                ) : null}
                <DataTable
                    columns={roleColumns}
                    items={roles}
                    rowKey={(row) => row.role}
                    caption="Roles on a space"
                    mobileCard={(row) => (
                        <div className="ark-settings-role-card">
                            {roleColumns[0].render(row)}
                            <span>{row.allows}</span>
                        </div>
                    )}
                />
            </div>
        </Panel>
    );
}
